import { useRef, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import {
  Button,
  cn,
  DateField,
  Label,
  Modal,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
} from '@atrvd/saas-ui'
import { formatCurrency, formatDate } from '@dcp/shared/utils/formatters'
import { useCreditCardTransactions } from '@/hooks/useCreditCardTransactions'
import { useTablePagination } from '@/hooks/useTablePagination'
import type { CreditCardAvailabilityRow } from '@/lib/db-tenant'
import type { CreditCardTransaction } from '@/lib/credit-card-transactions-db'
import { formatAccountNumber } from '@/components/ledgersync/connectionManagement.utils'
import { transactionCountLabel, transactionDescription } from './creditCardTransactions'

const COLUMN_COUNT = 3

/** The card fields the dialog needs, as Credit Card Availability returns them. */
export type CreditCardTransactionsCard = Pick<
  CreditCardAvailabilityRow,
  'id' | 'name' | 'account_number' | 'facility_name' | 'balance'
>

interface CreditCardTransactionsDialogProps {
  card: CreditCardTransactionsCard
  onClose: () => void
}

/**
 * "View transactions" for one credit card: the transactions behind its Current
 * Balance (Usage), newest first, in an optional From / To range.
 *
 * Mount it only while open, keyed by the card id: it opens on the card's whole
 * history (From and To empty) and reads the transactions only while mounted.
 *
 * Paged server-side with the app-wide sizes (20/50/100); a new range goes back
 * to page 1. The footer count and total cover the WHOLE range, every page, as
 * the server computes them — not the balance; the two need not match (see
 * `creditCardTransactionsDb`).
 */
export function CreditCardTransactionsDialog({ card, onClose }: CreditCardTransactionsDialogProps) {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  /**
   * `min`/`max` only grey out calendar days; a typed date still reaches state,
   * so "To before From" is reachable and is reported instead of fetched.
   */
  const rangeInverted = Boolean(from && to && from > to)
  const pagination = useTablePagination({
    // The total comes back with the page (`total_count`), see `forTotal` below.
    totalItems: undefined,
    // Page N of another range is an arbitrary slice.
    resetKey: JSON.stringify([from, to]),
  })
  const { data, error, isFetching, isPlaceholderData, refetch } = useCreditCardTransactions(
    rangeInverted
      ? null
      : {
          bankAccountId: card.id,
          from: from || null,
          to: to || null,
          page: pagination.page,
          pageSize: pagination.pageSize,
        },
  )
  // An idle (inverted-range) query may still hold the previous range's placeholder.
  const result = rangeInverted ? undefined : data
  const rows = result?.rows
  // The rows on screen belong to the previous range or page until the new one
  // arrives: dimmed, and its count and total withheld, so neither reads as the
  // new range's.
  const stale = result !== undefined && isPlaceholderData
  const { tablePaginationProps } = pagination.forTotal({ total: result?.totalCount, isFetching })
  // The pager sits below the table, so after Next the body stays scrolled to the
  // bottom and the new page would open at its last rows; bring its first rows back.
  const tableTop = useRef<HTMLDivElement>(null)
  const showFromTop = <A extends unknown[]>(change: (...args: A) => void) => (...args: A) => {
    change(...args)
    tableTop.current?.scrollIntoView({ block: 'start' })
  }
  const retry = () => void refetch()

  const clearRange = () => {
    setFrom('')
    setTo('')
  }

  const mask = card.account_number ? ` ${formatAccountNumber(card.account_number)}` : ''

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Current Balance (Usage)"
      description={`${card.name}${mask} · ${card.facility_name}`}
      size="xl"
      footer={
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            {result && !stale ? transactionCountLabel(result.totalCount) : '—'}
          </span>
          <span className="ml-auto whitespace-nowrap font-semibold tabular-nums">
            Total of shown transactions: {result && !stale ? formatCurrency(result.totalAmount) : '—'}
          </span>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-[165px] space-y-1.5">
          <Label htmlFor="cc-tx-from">From</Label>
          <DateField
            id="cc-tx-from"
            value={from}
            onValueChange={setFrom}
            max={to || undefined}
          />
        </div>
        <div className="w-[165px] space-y-1.5">
          <Label htmlFor="cc-tx-to">To</Label>
          <DateField
            id="cc-tx-to"
            value={to}
            onValueChange={setTo}
            min={from || undefined}
          />
        </div>
        {(from || to) && (
          <Button type="button" variant="ghost" onClick={clearRange}>
            <X aria-hidden="true" className="h-4 w-4" />
            Clear
          </Button>
        )}
        <div className="ml-auto flex items-baseline gap-2 whitespace-nowrap">
          <span className="text-sm text-muted-foreground">Current Balance (Usage)</span>
          <span className="text-lg font-semibold tabular-nums">
            {card.balance === null ? '—' : formatCurrency(card.balance)}
          </span>
        </div>
      </div>

      {/* A failed REFRESH keeps the rows already loaded and says so beside them. */}
      {rows && error && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <span>{error.message || 'Could not refresh the transactions'}</span>
          <Button type="button" variant="outline" size="sm" onClick={retry}>
            Retry
          </Button>
        </div>
      )}

      <div ref={tableTop} className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Transaction Date</TableHead>
              <TableHead>Amount</TableHead>
              {/* Date and Amount never wrap, so Description takes the rest of the width. */}
              <TableHead className="w-full">Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody aria-busy={stale} className={cn(stale && 'opacity-50 transition-opacity')}>
            {rangeInverted ? (
              <MessageRow>To is earlier than From — no date can fall in that range.</MessageRow>
            ) : rows === undefined && error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} role="alert" className="text-destructive">
                  <div className="flex items-center justify-between gap-3">
                    <span>{error.message || 'Failed to load the transactions'}</span>
                    <Button type="button" variant="outline" size="sm" onClick={retry}>
                      Retry
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : rows === undefined ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} role="status">
                  {isFetching ? 'Loading transactions…' : null}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <MessageRow>No transactions in this date range.</MessageRow>
            ) : (
              rows.map((row) => <TransactionRow key={row.id} row={row} />)
            )}
          </TableBody>
        </Table>
      </div>

      {/* Below the table, outside the Modal footer, as in ViewBankTransactionsDialog:
          the footer figures cover every page, so they must not read as this
          page's subtotal. Kept on a failed page so the user can step back. */}
      {tablePaginationProps && !rangeInverted && (
        <TablePagination
          {...tablePaginationProps}
          onPageChange={showFromTop(tablePaginationProps.onPageChange)}
          onPageSizeChange={
            tablePaginationProps.onPageSizeChange && showFromTop(tablePaginationProps.onPageSizeChange)
          }
          itemName="transactions"
          className="mt-4"
        />
      )}
    </Modal>
  )
}

function MessageRow({ children }: { children: ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={COLUMN_COUNT} className="py-8 text-center text-muted-foreground">
        {children}
      </TableCell>
    </TableRow>
  )
}

function TransactionRow({ row }: { row: CreditCardTransaction }) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">
        <span className="inline-flex items-center gap-2 tabular-nums">
          {formatDate(row.transaction_date)}
          {row.pending === true && (
            <>
              {/* The badge is hidden from assistive tech; the meaning travels as text. */}
              <span className="sr-only">Not yet posted by the bank</span>
              <span aria-hidden="true">
                <StatusBadge status="pending" label="Pending" />
              </span>
            </>
          )}
        </span>
      </TableCell>
      {/* Signed as stored: a charge on the card is negative. */}
      <TableCell className="whitespace-nowrap tabular-nums">{formatCurrency(row.amount)}</TableCell>
      {/* Wraps so the full text is always readable; `anywhere` (not `break-word`)
          also lets a long unspaced token shrink the column instead of widening the table. */}
      <TableCell className="[overflow-wrap:anywhere]">{transactionDescription(row)}</TableCell>
    </TableRow>
  )
}
